import { useEffect } from "react";
import { createRootRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Button, Container, Section } from "@alight/ui";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  // The Host Portal (/admin/*) carries its own slim chrome — the public Nav
  // and Footer would only distract the team there.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");

  // index.html ships a static Organization/WebSite JSON-LD fallback for
  // crawlers that never run JS (Bing, most AI crawlers) — see its comment.
  // The SPA shell (index.html) is served for EVERY route, not just "/", so
  // this runs once per full page load regardless of which route a visitor
  // or the prerenderer lands on first, and drops it in favour of the
  // CMS-accurate <JsonLd> the home route renders in its own JSX. Without
  // this, every non-home route (which never renders that JsonLd itself)
  // would keep the home page's fallback graph stuck in its <head> forever.
  useEffect(() => {
    document.getElementById("fallback-org-jsonld")?.remove();
    document.getElementById("fallback-website-jsonld")?.remove();
  }, []);
  if (isAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-paper">
        <Outlet />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col bg-paper">
      {/* Skip link for keyboard / screen-reader users. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-card focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-fg focus:shadow-card"
      >
        Skip to content
      </a>
      <Nav />
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function NotFound() {
  return (
    <Section spacing="lg">
      <Container width="narrow" className="text-center">
        <p className="text-sm font-bold uppercase tracking-wide text-accent">404</p>
        <h1 className="mt-2 font-heading text-4xl font-extrabold text-ink">
          Page not found
        </h1>
        <p className="mx-auto mt-4 max-w-md text-lg text-muted">
          Sorry, we couldn&apos;t find that page. It may have moved, or the link may be
          incomplete.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/">Back to home</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/sessions">This season's sessions</Link>
          </Button>
        </div>
      </Container>
    </Section>
  );
}
